import React, { useCallback } from 'react';

import { Anchor, Box, Group, Skeleton, Stack, Text, ThemeIcon, useMantineTheme } from '@mantine/core';
import { IconCircleCheck } from '@tabler/icons-react';
import WorkoutDetail from 'components/Workout/WorkoutDetail';
import PreviousClassNotes from 'components/workout-program/PreviousClassNotes';
import WorkoutProgramEmptyState from 'components/workout-program/WorkoutProgramEmptyState';
import useClassWorkoutDetails from 'hooks/workout/useClassWorkoutDetails';
import { buildPreviewDataFromWorkout } from 'pages/private/owner/Workouts/Programming/components/AssignWorkoutModal/utils/templateToWorkoutWrite';
import { useNavigate } from 'react-router-dom';

interface ClassWorkoutViewProps {
  kiloClassId: string | undefined;
  legacyNotes?: string | null;
}

const ClassWorkoutView = ({ kiloClassId, legacyNotes }: ClassWorkoutViewProps): JSX.Element => {
  const { data: workout, isLoading, isError } = useClassWorkoutDetails({ kiloClassId });
  const navigate = useNavigate();
  const theme = useMantineTheme();

  const handleGoToWhiteboard = useCallback(() => {
    navigate('/workouts/whiteboard', {
      state: {
        kiloClassId,
        kiloClassTypeId: workout?.data?.kiloClassType?.kiloClassTypeId,
        date: workout?.data?.date,
      },
    });
  }, [navigate, kiloClassId, workout?.data]);

  if (isLoading) {
    return <Skeleton h={72} radius='md' />;
  }

  if (isError) {
    return (
      <Stack spacing='sm'>
        <WorkoutProgramEmptyState isError />
        <PreviousClassNotes notes={legacyNotes} />
      </Stack>
    );
  }

  if (!workout?.data) {
    return (
      <Stack spacing='sm'>
        <WorkoutProgramEmptyState />
        <PreviousClassNotes notes={legacyNotes} />
      </Stack>
    );
  }

  return (
    <Stack spacing='sm'>
      <Group position='apart' align='center'>
        <Text size='xs' color='dimmed' tt='uppercase' fw={700}>
          Workout Program
        </Text>
        <Anchor size='sm' onClick={handleGoToWhiteboard} mr='xs'>
          View Whiteboard
        </Anchor>
      </Group>
      <Box
        py='md'
        px='lg'
        sx={{
          borderRadius: theme.radius.md,
          border: `1px solid ${theme.colors.green[3]}`,
        }}
      >
        <Group spacing='sm' noWrap position='apart'>
          <Group spacing='sm' noWrap>
            <ThemeIcon color='green' variant='light' radius='xl' size='md'>
              <IconCircleCheck size={20} />
            </ThemeIcon>
            <Text color='gray.8' fw={600} lineClamp={2}>
              {workout.data.name}
            </Text>
          </Group>
        </Group>
      </Box>

      <WorkoutDetail template={buildPreviewDataFromWorkout(workout.data)} />
    </Stack>
  );
};

export default ClassWorkoutView;
